import React, { useCallback, useRef } from "react";
import { List } from "../../Components/common/List/List.jsx";
import { ListProvider } from "../../Components/common/List/Providers/List.jsx";
import { createModalConfig } from "../../Models/ModalConfig.ts";
import { Modal, ModalBody } from "reactstrap";
import { useCollectionIndexContext } from "./providers/";
import { useListProps } from "./utilities/list.tsx";
import CollectionListItem from "./CollectionListItem.tsx";
import { Collection } from "../../Models/Collection.ts";
import { CollectionHandlerProvider } from "./providers/CollectionHandlerProvider.tsx";
import { CollectionHandler } from "./CollectionHandler.tsx";
import CollectionDelete from "./CollectionDelete.tsx";
import { CollectionDeleteProvider } from "./providers/CollectionDeleteProvider.tsx";
import CollectionBook from "./CollectionBook.tsx";
import { CollectionBookProvider } from "./providers/CollectionBookProvider.tsx";

/* ===========================================================================
 * TYPE OVERRIDES (Legacy/Generic Components)
 * =========================================================================== */
const AnyListProvider = ListProvider as any;
const AnyList = List as any;

/* ===========================================================================
 * COMPONENT: Collections
 * =========================================================================== */

/**
 * @component Collections
 *
 * The main container for the Collections module.
 * This component acts as an orchestrator that:
 * 1. Renders a paginated list of collections using a generic List component.
 * 2. Manages a centralized Modal system that dynamically switches providers
 * based on the requested action (Create, Edit, Details, Delete, or Book).
 * 3. Handles UI refreshes via `listRef` after successful operations.
 *
 * @returns {JSX.Element} The collection index page including list and modals.
 */
export const Collections: React.FC = () => {
  /** Reference to the List component to trigger manual refreshes (e.g., after a delete). */
  const listRef = useRef<{ refresh: () => void } | null>(null);

  /** Hook providing configuration for path, columns, filters, and massive actions. */
  const listProps = useListProps();

  /** State and setter for modal visibility and context-specific data. */
  const { modalConfig, setModalConfig } = useCollectionIndexContext();

  /* ---------------------------------------------------------------------------
   * HELPER FUNCTIONS
   * ------------------------------------------------------------------------- */

  /**
   * Renders an individual row in the collection list.
   * Memorized to prevent unnecessary re-renders of the entire list.
   */
  const renderItem = useCallback((item: Collection, index: number) => {
    return <CollectionListItem listRef={listRef} item={item} index={index} />;
  }, []);

  /**
   * Resets the modal configuration to its default (closed) state.
   */
  const closeModal = () => {
    if (setModalConfig) {
      setModalConfig(createModalConfig());
    }
  };

  /* ---------------------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------------------- */

  return (
    <>
      {/* List Section: Handles data fetching, filtering, and pagination */}
      <AnyListProvider
        path={listProps.path}
        sorting={listProps.sorting}
        filters={listProps.filters}
        defaultDate={listProps.defaultDate}
      >
        <AnyList
          ref={listRef}
          title={listProps.title}
          create={listProps.create}
          banner={listProps.banner}
          massiveActions={listProps.massiveActions}
          renderItem={renderItem}
          pagination={listProps.pagination}
          omnisearch={listProps.omnisearch}
          selectRowsEnabled={listProps.selectRowsEnabled}
        />
      </AnyListProvider>

      {/* Centralized Modal System */}
      <Modal isOpen={modalConfig?.isOpen} size={modalConfig?.size}>
        <ModalBody className={modalConfig?.className || ""}>
          {/* Workflow: Create, Edit, or View Details */}
          {(modalConfig?.action === "create" ||
            modalConfig?.action === "edit" ||
            modalConfig?.action === "details") && (
            <CollectionHandlerProvider
              title={modalConfig.title || ""}
              standAlone={true}
              id={modalConfig?.data?.[0]}
              autosave={false}
              callback={() => {
                closeModal();
                listRef.current?.refresh();
              }}
              action={modalConfig?.action}
            >
              <CollectionHandler />
            </CollectionHandlerProvider>
          )}

          {/* Workflow: Batch or Single Deletion */}
          {modalConfig.action === "delete" && (
            <CollectionDeleteProvider
              data={modalConfig.data}
              callback={() => {
                listRef.current?.refresh();
                closeModal();
              }}
            >
              <CollectionDelete />
            </CollectionDeleteProvider>
          )}

          {/* Workflow: Batch or Single Booking */}
          {modalConfig.action === "book" && (
            <CollectionBookProvider
              data={modalConfig.data}
              callback={() => {
                listRef.current?.refresh();
                closeModal();
              }}
            >
              <CollectionBook />
            </CollectionBookProvider>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default Collections;
